import { Spin } from "antd";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotification } from "~/hooks";
import { useCopyBooking, useGetBooking } from "~/services/booking";
import { capitalize } from "~/utils";
import { CopyOptionsForm } from "./copy-options-form";
import { FormValues, TBookingPreview } from "./types";

export const CopyBooking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success } = useNotification();

  const { data: bookingData, isLoading: isLoadingBooking } = useGetBooking(id);
  const mutation = useCopyBooking();

  const selectedBooking = useMemo<TBookingPreview | undefined>(() => {
    if (!bookingData) return;
    const { client, project, bookingDate, serviceTicket } = bookingData;
    const formattedServices =
      serviceTicket?.map((ticket) => ({
        id: ticket.id,
        type: capitalize(ticket.serviceType),
        serviceType: ticket.serviceType,
        location: ticket.location
          ? [ticket.location.address, ticket.location.city, ticket.location.state]
              .filter(Boolean)
              .join(", ")
          : ticket.isAhcWarehouse
          ? "AHC Warehouse"
          : "",
        date: ticket.serviceDatetime
          ? dayjs(ticket.serviceDatetime).format("YYYY-MM-DD")
          : "",
        vesselId: ticket.vesselId,
        vesselName: ticket.vessel?.name,
        inventoryCount: ticket.inventories?.length || 0,
      })) || [];
    const formattedInventory =
      serviceTicket?.reduce<TBookingPreview["inventory"]>((acc, ticket) => {
        const inventoryItems =
          ticket.inventories?.map((item) => ({
            id: item.id,
            inventoryRef: item.inventoryRef ?? "",
            title: item.title || "",
            dimensions: `${item.width}" × ${item.height}" × ${item.length}"`,
          })) || [];
        return [...acc, ...inventoryItems];
      }, []) || [];
    return {
      id: bookingData.id,
      client:
        [client?.companyName || client?.firstName, client?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim() || "Unnamed Client",
      project: project?.name,
      date: dayjs(bookingDate).format("YYYY-MM-DD"),
      services: formattedServices,
      inventory: formattedInventory,
      referenceNumber: bookingData.referenceNumber,
    };
  }, [bookingData]);

  const onCopyBooking = (values: FormValues) => {
    if (!selectedBooking) return;
    const hasSelectedServices = values.serviceTicketIds?.length > 0;

    const overrides: Record<string, { serviceDatetime?: string; vesselId?: string }> = {};
    if (values.serviceTicketOverrides) {
      for (const [stId, override] of Object.entries(values.serviceTicketOverrides)) {
        if (override.serviceDatetime || override.vesselId !== undefined) {
          overrides[stId] = override;
        }
      }
    }

    mutation.mutate(
      {
        id: selectedBooking.id,
        copyClientInformation: values.copyClient,
        copyInventoryItems: values.copyInventory,
        copyServiceTypes: hasSelectedServices,
        copyDates: true,
        newBookingDate: new Date().toISOString(),
        serviceTicketIds: hasSelectedServices
          ? values.serviceTicketIds
          : undefined,
        serviceTicketOverrides:
          Object.keys(overrides).length > 0 ? overrides : undefined,
        isReturn: values.isReturn || false,
      },
      {
        onSuccess: () => {
          success({ message: "Booking duplicated successfully" });
          navigate(-1);
        },
      }
    );
  };

  return (
    <Spin spinning={isLoadingBooking}>
      <div className="w-full bg-gray-50 py-6 px-6">
        <CopyOptionsForm
          selectedBooking={selectedBooking}
          onSubmit={onCopyBooking}
          isSubmitting={mutation.isPending}
        />
      </div>
    </Spin>
  );
};
